import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, LogOut, Rocket, Users, MessageSquareHeart, Send, Lightbulb, Star, CheckCircle2, Scale, ThumbsUp, ThumbsDown, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";
import Seo from "../../components/Seo";

export default function AmbassadorSpace() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [mine, setMine] = useState([]);
  const [fb, setFb] = useState({ rating: 0, liked: "", improve: "", message: "" });
  const [sending, setSending] = useState(false);
  const [ideas, setIdeas] = useState([]);

  const load = () => {
    api.get("/ambassador/space").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/ambassador/feedback/mine").then((r) => setMine(r.data)).catch(() => {});
    if (user?.committee) api.get("/ambassador/committee/ideas").then((r) => setIdeas(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.committee]);

  const vote = async (pid, v, comment) => {
    try {
      await api.post("/ambassador/committee/vote", { project_id: pid, vote: v, comment: comment || "" });
      toast.success("Votre avis a été enregistré. Merci !");
      api.get("/ambassador/committee/ideas").then((r) => setIdeas(r.data)).catch(() => {});
    } catch (err) { toast.error(apiErr(err)); }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!fb.message.trim() && !fb.liked.trim() && !fb.improve.trim()) return toast.error("Partagez au moins un retour avant d'envoyer.");
    setSending(true);
    try {
      await api.post("/ambassador/feedback", { ...fb, rating: fb.rating || null });
      toast.success("Merci ! Votre retour a bien été transmis à l'Academy.");
      setFb({ rating: 0, liked: "", improve: "", message: "" });
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  return (
    <div className="grain min-h-screen bg-void">
      <Seo title="Espace Ambassadeur" description="Espace privé du Comité des Ambassadeurs Académiques." path="/ambassadeur/espace" noindex />

      {/* Barre supérieure */}
      <header className="glass sticky top-0 z-20 flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-8">
        <Link to="/"><Logo size={26} /></Link>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-ink-secondary sm:block">Ambassadeur · <span className="text-white">{user?.first_name}</span></span>
          <button onClick={() => { logout(); nav("/"); }} data-testid="ambassador-logout" className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary transition-colors hover:text-white">
            <LogOut size={16} /> Quitter
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        {/* Hero de bienvenue */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-surface p-8"
          data-testid="ambassador-hero"
        >
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-cyan/20 blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-electric/15 blur-[110px]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-cyan">
              <Sparkles size={12} /> Comité des Ambassadeurs
            </div>
            <h1 className="mt-4 font-display text-3xl font-900 text-white sm:text-4xl">
              Bienvenue{user?.first_name ? `, ${user.first_name}` : ""}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-secondary">
              Vous faites désormais partie du cercle privé des ambassadeurs académiques. Découvrez
              l'application en avant-première, testez-la librement et partagez vos retours privilégiés
              qui feront évoluer le produit.
            </p>
          </div>
        </motion.div>

        {/* Statistiques personnelles */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Stat icon={Lightbulb} color="#00F0FF" label="Vos idées déposées" value={info?.ideas_count ?? "—"} testid="amb-stat-ideas" />
          <Stat icon={MessageSquareHeart} color="#BF5AF2" label="Vos retours envoyés" value={info?.feedback_count ?? "—"} testid="amb-stat-feedback" />
        </div>

        {/* Accès avant-première + comité */}
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-xl border border-white/10 bg-surface p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan/10 text-cyan"><Rocket size={20} /></span>
            <h2 className="mt-4 font-display text-lg font-700 text-white">Accès avant-première</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
              Découvrez l'application depuis sa page d'accueil : parcourez l'intégralité des informations,
              testez l'expérience de bout en bout et déposez une idée test.
            </p>
            <Link to="/" data-testid="amb-try-app" className="group mt-5 inline-flex items-center gap-2 rounded-full bg-cyan px-5 py-3 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan">
              Découvrir l'application <Rocket size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="rounded-xl border border-white/10 bg-surface p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-electric/10 text-electric"><Users size={20} /></span>
            <h2 className="mt-4 font-display text-lg font-700 text-white">Le rôle d'ambassadeur</h2>
            <ul className="mt-3 space-y-2 text-sm text-ink-secondary">
              <li className="flex gap-2"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-cyan" /> Tester l'application en avant-première</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-cyan" /> Partager des retours privilégiés et confidentiels</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-cyan" /> Faire partie du comité qui façonne le produit</li>
            </ul>
          </motion.div>
        </div>

        {/* Comité de décision (si membre) */}
        {user?.committee && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="mt-6 rounded-xl border border-amber-400/25 bg-surface p-6 sm:p-8" data-testid="amb-committee-card">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400"><Scale size={20} /></span>
              <div>
                <h2 className="font-display text-lg font-700 text-white">Comité de décision</h2>
                <p className="text-sm text-ink-secondary">Donnez votre avis sur les idées en cours de sélection.</p>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {ideas.length === 0 && <p className="text-sm text-ink-tertiary" data-testid="amb-committee-empty">Aucune idée à évaluer pour le moment. Revenez bientôt !</p>}
              {ideas.map((idea) => <CommitteeIdea key={idea.id} idea={idea} onVote={vote} />)}
            </div>
          </motion.div>
        )}

        {/* Formulaire de retour */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-6 rounded-xl border border-white/10 bg-surface p-6 sm:p-8" data-testid="amb-feedback-card">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan/10 text-cyan"><MessageSquareHeart size={20} /></span>
            <div>
              <h2 className="font-display text-lg font-700 text-white">Votre retour privilégié</h2>
              <p className="text-sm text-ink-secondary">Aidez-nous à améliorer l'application.</p>
            </div>
          </div>

          <form onSubmit={submitFeedback} className="mt-6 space-y-4" data-testid="amb-feedback-form">
            <div>
              <span className="mb-2 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Note globale</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setFb({ ...fb, rating: n })} data-testid={`amb-rating-${n}`} className="p-1">
                    <Star size={26} className={n <= fb.rating ? "fill-yellow-400 text-yellow-400" : "text-ink-tertiary"} />
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Ce que vous avez aimé" value={fb.liked} onChange={(e) => setFb({ ...fb, liked: e.target.value })} testid="amb-fb-liked" />
            <Textarea label="Ce qui pourrait être amélioré" value={fb.improve} onChange={(e) => setFb({ ...fb, improve: e.target.value })} testid="amb-fb-improve" />
            <Textarea label="Message libre" value={fb.message} onChange={(e) => setFb({ ...fb, message: e.target.value })} testid="amb-fb-message" />
            <button type="submit" disabled={sending} data-testid="amb-feedback-submit" className="group inline-flex items-center gap-2 rounded-full bg-cyan px-6 py-3.5 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60">
              {sending ? "Envoi…" : "Envoyer mon retour"} <Send size={15} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>

          {mine.length > 0 && (
            <div className="mt-8 border-t border-white/10 pt-6" data-testid="amb-feedback-history">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Vos retours précédents</div>
              <div className="mt-4 space-y-3">
                {mine.map((m) => (
                  <div key={m.id} className="rounded-lg border border-white/10 bg-void p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={m.rating && n <= m.rating ? "fill-yellow-400 text-yellow-400" : "text-ink-tertiary"} />)}
                      </div>
                      <span className="text-[11px] text-ink-tertiary">{m.at ? new Date(m.at).toLocaleDateString("fr-FR") : ""}</span>
                    </div>
                    {m.message && <p className="mt-2 text-sm text-ink-secondary">{m.message}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function CommitteeIdea({ idea, onVote }) {
  const [comment, setComment] = useState(idea.my_comment || "");
  const opts = [
    { v: "pour", label: "Pour", icon: ThumbsUp, cls: "border-green-500/40 text-green-400 hover:bg-green-500/10", active: "border-green-500 bg-green-500/15 text-green-400" },
    { v: "contre", label: "Contre", icon: ThumbsDown, cls: "border-red-500/40 text-red-400 hover:bg-red-500/10", active: "border-red-500 bg-red-500/15 text-red-400" },
    { v: "abstention", label: "Abstention", icon: MinusCircle, cls: "border-white/15 text-ink-secondary hover:bg-white/5", active: "border-white/40 bg-white/10 text-white" },
  ];
  return (
    <div className="rounded-xl border border-white/10 bg-void p-5" data-testid={`amb-committee-idea-${idea.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base font-700 text-white">{idea.title || "Idée"}</div>
          <div className="mt-0.5 text-xs text-ink-tertiary">{idea.category} · {idea.app_type} · {idea.status}</div>
        </div>
        {idea.my_vote && <span className="shrink-0 rounded-full bg-cyan/15 px-2.5 py-0.5 text-[11px] font-600 uppercase text-cyan">Voté</span>}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-ink-secondary">{idea.description}</p>
      <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Votre commentaire (optionnel)" data-testid={`amb-committee-comment-${idea.id}`}
        className="mt-4 w-full resize-none rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
      <div className="mt-3 flex flex-wrap gap-2">
        {opts.map((o) => (
          <button key={o.v} onClick={() => onVote(idea.id, o.v, comment)} data-testid={`amb-vote-${o.v}-${idea.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-600 transition-colors ${idea.my_vote === o.v ? o.active : o.cls}`}>
            <o.icon size={15} /> {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, label, value, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-xl border border-white/10 bg-surface p-5" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-0.5" style={{ background: color }} />
      <span className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ background: `${color}1f`, color }}><Icon size={20} /></span>
      <div className="mt-4 font-display text-4xl font-900 tabular-nums text-white">{value}</div>
      <div className="mt-1 text-sm text-ink-secondary">{label}</div>
    </motion.div>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</span>
      <textarea
        rows={3} value={value} onChange={onChange} data-testid={testid}
        className="w-full resize-none rounded-xl border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
      />
    </label>
  );
}
